import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem } from 'vnetwork-ui'
import { Loader2, Save } from 'lucide-react'
import dayjs from 'dayjs'
import {
    Autocomplete,
    FormControlLabel,
    Switch,
    TextField,
} from '@mui/material'

import { CommissionPolicy } from '@/modules/Affiliate/types'
import { getCommissionPolicyScopeFilterOptions } from '@/modules/Affiliate/constants'
import {
    getAffiliatesList,
    getProductGroupOptions,
    getProductOptions,
    getDepartmentOptions,
} from '@/modules/Affiliate/services'
import { PolicyFormData, createPolicySchema } from './schema'

// Audience referrer types; '' = applies to everyone (no narrowing).
const REFERRER_TYPE_VALUES = [
    '',
    'employee',
    'customer',
    'partner',
    'reseller',
    'affiliate',
    'other',
] as const

interface PolicyFormModalProps {
    open: boolean
    onClose: () => void
    policy?: CommissionPolicy | null
    onSubmit: (data: PolicyFormData) => Promise<void>
    isLoading?: boolean
    fixedAffiliateId?: number
    allowOwnerSelection?: boolean
}

type AffiliateOption = { label: string; value: number | '' }

const normalizeAffiliateResults = (response?: any) =>
    response?.results ||
    response?.Results ||
    response?.ResponseArray?.results ||
    response?.ResponseArray?.Results ||
    response?.result?.results ||
    response?.result?.Results ||
    []

const PolicyFormModal: React.FC<PolicyFormModalProps> = ({
    open,
    onClose,
    policy,
    onSubmit,
    isLoading = false,
    fixedAffiliateId,
    allowOwnerSelection = false,
}) => {
    const { t } = useTranslation('affiliate')
    const isEdit = !!policy

    const [productGroupOptions, setProductGroupOptions] = useState<
        Array<{ label: string; value: number }>
    >([])
    const [productOptions, setProductOptions] = useState<
        Array<{ label: string; value: number }>
    >([])
    const [departmentOptions, setDepartmentOptions] = useState<
        Array<{ label: string; value: number }>
    >([])
    const [affiliateOptions, setAffiliateOptions] = useState<AffiliateOption[]>(
        []
    )
    const [loadingOptions, setLoadingOptions] = useState(false)

    const {
        control,
        handleSubmit,
        watch,
        reset,
        setValue,
        formState: { errors },
    } = useForm<PolicyFormData>({
        resolver: yupResolver(createPolicySchema(t)),
        defaultValues: {
            policy_name: '',
            affiliate_id: fixedAffiliateId ?? '',
            affiliate_ids: fixedAffiliateId ? [fixedAffiliateId] : [],
            is_global: false,
            scope: 'default',
            commission_percent: 5,
            product_group_id: undefined,
            product_id: undefined,
            effective_date: dayjs().format('YYYY-MM-DD'),
            expired_date: undefined,
            priority: 0,
            description: '',
            referrer_type: '',
            department_ids: [],
        },
    })

    const scope = watch('scope')
    const isGlobal = watch('is_global')
    const referrerType = watch('referrer_type')

    // Departments only apply to employees; clear them whenever the type changes away.
    useEffect(() => {
        if (referrerType !== 'employee') {
            setValue('department_ids', [])
        }
    }, [referrerType, setValue])

    // Global and targeted are mutually exclusive; keep the list empty while the switch is on
    // so the payload can never claim both.
    useEffect(() => {
        if (isGlobal) {
            setValue('affiliate_ids', [])
        }
    }, [isGlobal, setValue])

    useEffect(() => {
        if (scope === 'default') {
            setValue('product_group_id', undefined)
            setValue('product_id', undefined)
        }
        if (scope === 'product_group') {
            setValue('product_id', undefined)
        }
        if (scope === 'product') {
            setValue('product_group_id', undefined)
        }
    }, [scope, setValue])

    // Fetch owner/product options on mount
    useEffect(() => {
        const fetchOptions = async () => {
            setLoadingOptions(true)
            try {
                const [groups, products, departments, affiliates] =
                    await Promise.all([
                        getProductGroupOptions(),
                        getProductOptions(),
                        getDepartmentOptions(),
                        allowOwnerSelection
                            ? getAffiliatesList({
                                  page: 1,
                                  page_size: 100,
                                  // Customer affiliates only — employee affiliates use default/global
                                  // policies, managed separately.
                                  referrer_type: 'non_employee',
                              } as any)
                            : Promise.resolve(undefined),
                    ])
                setProductGroupOptions(groups)
                setProductOptions(products)
                setDepartmentOptions(departments)
                const affiliateResults = normalizeAffiliateResults(affiliates)
                setAffiliateOptions([
                    { label: t('global_policy'), value: '' },
                    ...affiliateResults.map((affiliate: any) => ({
                        label:
                            affiliate.referrer_name ||
                            affiliate.referrer_company ||
                            affiliate.referral_code ||
                            `ID: ${affiliate.affiliate_id}`,
                        value: affiliate.affiliate_id,
                    })),
                ])
            } catch (error) {
                console.error('Failed to fetch options:', error)
            } finally {
                setLoadingOptions(false)
            }
        }
        if (open) {
            fetchOptions()
        }
    }, [open, allowOwnerSelection, t])

    useEffect(() => {
        if (policy) {
            reset({
                policy_name: policy.policy_name,
                affiliate_id: policy.affiliate_id || '',
                affiliate_ids: policy.is_global
                    ? []
                    : policy.affiliate_ids ||
                      (policy.affiliate_id ? [policy.affiliate_id] : []),
                is_global: policy.is_global ?? false,
                scope: policy.scope,
                commission_percent: policy.commission_percent,
                product_group_id: policy.product_group_id || undefined,
                product_id: policy.product_id || undefined,
                effective_date: policy.effective_date
                    ? dayjs(policy.effective_date).format('YYYY-MM-DD')
                    : dayjs().format('YYYY-MM-DD'),
                expired_date: policy.expired_date
                    ? dayjs(policy.expired_date).format('YYYY-MM-DD')
                    : undefined,
                priority: policy.priority,
                description: policy.description || '',
                referrer_type: policy.referrer_type || '',
                department_ids: policy.department_ids || [],
            })
        } else {
            reset({
                policy_name: '',
                affiliate_id: fixedAffiliateId ?? '',
                affiliate_ids: fixedAffiliateId ? [fixedAffiliateId] : [],
                is_global: false,
                scope: 'default',
                commission_percent: 5,
                product_group_id: undefined,
                product_id: undefined,
                effective_date: dayjs().format('YYYY-MM-DD'),
                expired_date: undefined,
                priority: 0,
                description: '',
                referrer_type: '',
                department_ids: [],
            })
        }
    }, [policy, reset, fixedAffiliateId])

    const handleFormSubmit = async (data: PolicyFormData) => {
        await onSubmit(data)
    }
    const selectableAffiliateOptions = affiliateOptions.filter(
        (option) => option.value !== ''
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={isEdit ? t('edit_policy') : t('create_policy')}
            maxWidth="md"
        >
            <form
                onSubmit={handleSubmit(handleFormSubmit)}
                className="space-y-4"
            >
                <FormItem
                    name="policy_name"
                    type="textfield"
                    control={control}
                    label={t('policy_name')}
                    placeholder={t('policy_name')}
                    error={!!errors.policy_name}
                    helperText={errors.policy_name?.message}
                />

                {allowOwnerSelection && (
                    <Controller
                        name="is_global"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!field.value}
                                        onChange={(_e, checked) =>
                                            field.onChange(checked)
                                        }
                                    />
                                }
                                label={
                                    <span>
                                        {t('apply_globally')}
                                        <span className="block text-xs text-gray-500">
                                            {t('apply_globally_hint')}
                                        </span>
                                    </span>
                                }
                            />
                        )}
                    />
                )}

                {allowOwnerSelection && !isGlobal && (
                    <Controller
                        name="affiliate_ids"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                multiple
                                disablePortal
                                filterSelectedOptions
                                loading={loadingOptions}
                                loadingText={t('loading')}
                                noOptionsText={t('no_affiliates_available')}
                                options={selectableAffiliateOptions}
                                value={selectableAffiliateOptions.filter(
                                    (option) =>
                                        (field.value || []).includes(
                                            Number(option.value)
                                        )
                                )}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) =>
                                    option.value === value.value
                                }
                                disabled={loadingOptions}
                                onChange={(_event, value) =>
                                    field.onChange(
                                        value.map((item) => Number(item.value))
                                    )
                                }
                                onBlur={field.onBlur}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('owner')}
                                        placeholder={
                                            loadingOptions
                                                ? t('loading')
                                                : t('select_affiliate')
                                        }
                                        error={!!errors.affiliate_ids}
                                        helperText={
                                            errors.affiliate_ids?.message ||
                                            t('select_affiliates_hint')
                                        }
                                    />
                                )}
                            />
                        )}
                    />
                )}

                {allowOwnerSelection && (
                    <div className="grid grid-cols-2 gap-4">
                        <FormItem
                            name="referrer_type"
                            type="select"
                            control={control}
                            label={t('audience_referrer_type')}
                            options={REFERRER_TYPE_VALUES.map((value) => ({
                                label: t(`referrer_type_${value || 'any'}`),
                                value,
                            }))}
                        />
                        {referrerType === 'employee' && (
                            <Controller
                                name="department_ids"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        multiple
                                        disablePortal
                                        filterSelectedOptions
                                        loading={loadingOptions}
                                        loadingText={t('loading')}
                                        options={departmentOptions}
                                        value={departmentOptions.filter(
                                            (option) =>
                                                (field.value || []).includes(
                                                    Number(option.value)
                                                )
                                        )}
                                        getOptionLabel={(option) =>
                                            option.label
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.value === value.value
                                        }
                                        disabled={loadingOptions}
                                        onChange={(_event, value) =>
                                            field.onChange(
                                                value.map((item) =>
                                                    Number(item.value)
                                                )
                                            )
                                        }
                                        onBlur={field.onBlur}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t(
                                                    'audience_departments'
                                                )}
                                                placeholder={t(
                                                    'select_departments'
                                                )}
                                                error={!!errors.department_ids}
                                                helperText={
                                                    errors.department_ids
                                                        ?.message ||
                                                    t(
                                                        'audience_departments_hint'
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                )}
                            />
                        )}
                    </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                    <FormItem
                        name="scope"
                        type="select"
                        control={control}
                        label={t('scope')}
                        options={getCommissionPolicyScopeFilterOptions(t)}
                    />
                    <FormItem
                        name="commission_percent"
                        type="textfield"
                        control={control}
                        label={t('commission_percent')}
                        placeholder="0-100"
                        inputProps={{ type: 'number', min: 0, max: 100 }}
                        error={!!errors.commission_percent}
                        helperText={errors.commission_percent?.message}
                    />
                </div>

                {scope === 'product_group' && (
                    <FormItem
                        name="product_group_id"
                        type="select"
                        control={control}
                        label={t('product_group')}
                        placeholder={
                            loadingOptions
                                ? t('loading')
                                : t('select_product_group')
                        }
                        options={productGroupOptions}
                        disabled={loadingOptions}
                        error={!!errors.product_group_id}
                        helperText={errors.product_group_id?.message}
                    />
                )}

                {scope === 'product' && (
                    <FormItem
                        name="product_id"
                        type="select"
                        control={control}
                        label={t('product')}
                        placeholder={
                            loadingOptions ? t('loading') : t('select_product')
                        }
                        options={productOptions}
                        disabled={loadingOptions}
                        error={!!errors.product_id}
                        helperText={errors.product_id?.message}
                    />
                )}

                <div className="grid grid-cols-2 gap-4">
                    <FormItem
                        name="effective_date"
                        type="datepicker"
                        control={control}
                        label={t('effective_date')}
                        error={!!errors.effective_date}
                        helperText={errors.effective_date?.message}
                    />
                    <FormItem
                        name="expired_date"
                        type="datepicker"
                        control={control}
                        label={t('expired_date')}
                    />
                </div>

                <FormItem
                    name="priority"
                    type="textfield"
                    control={control}
                    label={t('priority')}
                    placeholder="0"
                    inputProps={{ type: 'number' }}
                />

                <FormItem
                    name="description"
                    type="textarea"
                    control={control}
                    label={t('description')}
                    placeholder={t('description')}
                />

                <div className="flex items-center justify-end gap-3 pt-4">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        disabled={isLoading}
                        variant="contained"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                {isEdit ? t('updating') : t('creating')}
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-2" />
                                {t('save')}
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default PolicyFormModal
